import {ActionList, type SxProp} from '@primer/react'
import {BlankIssueItem, IssueTemplateItem, LinkTemplateItem, SecurityPolicyItem} from './IssueTemplateItem'
import type {RepositoryPickerRepositoryIssueTemplates$data as Templates} from '@github-ui/item-picker/repository'
import type {IssueCreatePayload} from '.'
import {getBlankIssue, getIssueTemplate, repoHasAvailableTemplates} from './utils/model'
import {LoadingSkeleton} from '@github-ui/skeleton'
import {LABELS, TEST_IDS} from './constants'
import type {TemplateListVariant} from './TemplateListPane'

type TemplateListProps = {
  templates: Templates
} & TemplateListSelectedProp &
  SxProp

export type TemplateListSelectedProp = {
  onTemplateSelected: (template: IssueCreatePayload) => void
  variant?: TemplateListVariant
}

export function TemplateList({templates, onTemplateSelected, variant, sx}: TemplateListProps) {
  if (!repoHasAvailableTemplates(templates) && !templates.isBlankIssuesEnabled) {
    return <NoTemplates />
  }

  return (
    <ActionList sx={sx} data-testid={TEST_IDS.templateList}>
      {templates.issueForms &&
        templates.issueForms.map(form => (
          <IssueTemplateItem
            id={form.__id}
            key={`issue_forms.${form.name}`}
            onTemplateSelected={() => onTemplateSelected(getIssueTemplate(form))}
            name={form.name}
            about={form.description}
            variant={variant}
          />
        ))}
      {templates.issueTemplates &&
        templates.issueTemplates.map(template => (
          <IssueTemplateItem
            id={template.__id}
            key={`issue_templates.${template.name}`}
            onTemplateSelected={() => onTemplateSelected(getIssueTemplate(template))}
            name={template.name}
            about={template.about}
            variant={variant}
          />
        ))}
      {templates.isBlankIssuesEnabled && (
        <BlankIssueItem
          key="blank_issue"
          variant={variant}
          onTemplateSelected={() => onTemplateSelected(getBlankIssue())}
        />
      )}
      {templates.isSecurityPolicyEnabled && (
        <SecurityPolicyItem key="security_policy" link={templates.securityPolicyUrl} variant={variant} />
      )}
      {templates.contactLinks &&
        templates.contactLinks.map(link => (
          <LinkTemplateItem
            id={link.__id}
            key={`contact_links.${link.name}`}
            name={link.name}
            about={link.about}
            link={link.url}
            variant={variant}
          />
        ))}
    </ActionList>
  )
}

export function TemplateListLoading() {
  const loadingSkeletonWidths = ['220px', '250px', '290px', '210px']

  return (
    <>
      {loadingSkeletonWidths.map((width, index) => (
        <LoadingSkeleton key={index} sx={{m: 2}} variant="rounded" height="xl" width={width} />
      ))}
    </>
  )
}

export function NoTemplates({id}: {id?: string}) {
  return <span id={id}>{LABELS.noTemplates}</span>
}

try{ TemplateList.displayName ||= 'TemplateList' } catch {}
try{ TemplateListLoading.displayName ||= 'TemplateListLoading' } catch {}
try{ NoTemplates.displayName ||= 'NoTemplates' } catch {}